import { useCheckoutSession } from '../form/use_checkout_session.js';
import { useMultiTravelerCheckoutForm } from '../form/use_multi_traveler_checkout_form.js';
import { checkoutOfferModel } from '../model/offer_model.js';
import { policyViolationsOf, productContract } from '../model/product_contract.js';
import { checkoutScreenComposition } from '../products/checkout_screen.js';
import { CheckoutDrawerShell } from './checkout_drawer_shell.js';
import type { OfferCheckoutProps, WithRoster } from './checkout_subject.js';
import { useCheckoutEnv } from './env_context.js';
import { MultiTravelerCheckoutForm } from './multi_traveler_checkout_form.js';

/** Modo `offer`: a compra online de uma oferta com preço. Liga preço, farol de
 *  orçamento e política pelo modelo da oferta (`checkoutOfferModel`), o mesmo
 *  que o card 1-Click lê. O dono do rateio é o do produto, salvo quando o host
 *  o troca. O farol só é consultado quando o rateio tem um dono único
 *  (`'requester'`): com todo bloco alocando seriam N faróis. Ele nunca bloqueia
 *  o envio. */
export function OfferCheckout({
  open,
  readOnly,
  subject,
  travelers,
  rosterFailed,
  requesterId,
  tripId,
  policyTravelerId,
  allocationOwner: hostAllocationOwner,
  initialDraft,
  prefill,
  closeOnSubmit,
  submitting: hostSubmitting,
  submitNotice,
  onClose,
  onSubmit,
}: Readonly<WithRoster<OfferCheckoutProps>>) {
  const env = useCheckoutEnv();
  const { copy } = env;
  const { item } = subject;
  // O modelo agrega a política sobre TODOS os itens (um multi-trecho só é
  // dentro se cada trecho for) e escala o preço onde o produto manda.
  const offer = checkoutOfferModel({
    // Sem a lista (ou com ela vazia), o carrinho é o próprio item da oferta.
    items: subject.items?.length ? subject.items : [item],
    travelerCount: travelers.length,
    allocationOwner: hostAllocationOwner,
    offerFields: env.rules.offerFields,
  });
  // Inalcançável: o carrinho tem ao menos o item da oferta.
  if (!offer) throw new Error('checkout de oferta sem itens — reabra o checkout');
  const { items, withinPolicy, fields, total, allocationOwner } = offer;
  const form = useMultiTravelerCheckoutForm({
    io: env.io,
    rules: env.rules,
    productType: offer.productType,
    fields,
    open,
    travelers,
    requesterId,
    policyTravelerId,
    withinPolicy,
    allocationOwner,
    guestsPerRoom: productContract(item).guestsPerRoom(item),
    // O farol consulta o MESMO total que o painel de preço mostra.
    total: total.amount,
    startDate: offer.startDate,
    ...(initialDraft ? { initialDraft } : {}),
    ...(prefill ? { prefill } : {}),
  });
  // A tela do produto, uma vez por render: a casca e o form leem dela.
  const composition = checkoutScreenComposition(items, env);
  const session = useCheckoutSession({ form, hostSubmitting, closeOnSubmit, onClose });

  if (!open) return null;

  const handleSubmit = () => {
    if (session.submitting || !form.canSubmit) return;
    const submission = {
      perTraveler: session.travelerEntries(),
      motiveId: form.motiveId,
      justificationId: form.justificationId,
      observation: form.observation,
      policyTravelerId: form.policyTravelerId,
      allocationOwner,
      requesterId,
    };
    session.send(() => {
      onSubmit({ mode: 'offer', form: submission });
    });
  };

  return (
    <CheckoutDrawerShell
      phase={rosterFailed ? 'error' : form.phase}
      // Só o form que caiu se refaz (#6644); o roster que falhou fica no ✕.
      {...(!rosterFailed && form.phase === 'error' ? { onRetry: form.reload } : {})}
      items={items}
      composition={composition}
      tripId={tripId}
      travelers={travelers}
      perTraveler={offer.perTraveler}
      total={total}
      // `total` já é o do grupo, então total/unidades é a diária média do grupo.
      units={offer.units}
      submitting={session.submitting}
      canSubmit={!readOnly && form.canSubmit}
      {...(readOnly ? {} : { submitBlockers: form.submitBlockers })}
      submitNotice={submitNotice}
      onSubmit={handleSubmit}
      onClose={session.close}
    >
      <MultiTravelerCheckoutForm
        fields={fields}
        state={form}
        requesterId={requesterId}
        withinPolicy={withinPolicy}
        total={total.amount}
        allocationOwner={allocationOwner}
        allocationHint={copy.traveler.allocationFromRequester}
        outOfPolicyHint={copy.traveler.outOfPolicyReasons(policyViolationsOf(items))}
        motiveTitle={composition.motive.title}
      />
    </CheckoutDrawerShell>
  );
}
